import {
  Alert,
  Button,
  Group,
  MenuButton,
  type MenuButtonItem,
  Modal,
  Stack,
  Tabs,
  TabsList,
  TabsTrigger,
  Text,
  Tile,
  Title,
} from "@assetwatch/ui";
import {
  type ReactNode,
  type RefObject,
  useEffect,
  useRef,
  useState,
} from "react";
import { assigneesChanged, assigneeValue } from "../../lib/assigneeTokens";
import type { WorkOrderId } from "../../lib/workIds";
import type { WorkOrdersLocationSearch } from "../../lib/workOrdersSearch";
import { useMarkWorkOrderViewedMutation } from "../../mutations/useMarkWorkOrderViewedMutation";
import { usePrepareWorkOrderCompletionMutation } from "../../mutations/usePrepareWorkOrderCompletionMutation";
import {
  useProcedureAttachmentQuery,
  useUpdateProcedureAttachmentMutation,
} from "../../queries/useProcedureAttachment";
import { useProcedureExecutionQuery } from "../../queries/useProcedureExecution";
import { useTeamsUsersQuery } from "../../queries/useTeamsUsersQuery";
import {
  useWorkOrderAssignmentMutations,
  useWorkOrderAssignmentsQuery,
  type WorkOrderAssignment,
} from "../../queries/useWorkOrderAssignmentMutations";
import type { BulkActionOutcome } from "../../queries/useWorkOrderBulkActionsMutations";
import {
  useWorkOrderDetailQuery,
  type WorkOrderDetail,
} from "../../queries/useWorkOrderDetailQuery";
import { useWorkOrderLookupsQuery } from "../../queries/useWorkOrderLookupsQuery";
import {
  useCancelWorkOrderMutation,
  useCompleteWorkOrderMutation,
  useDeleteWorkOrderMutation,
} from "../../queries/useWorkOrderMutations";
import type { WorkOrder } from "../../queries/useWorkOrdersQuery";
import {
  useWorkOrderUpdateMutation,
  WorkOrderUpdateError,
} from "../../queries/useWorkOrderUpdateMutation";
import { WorkSplitView } from "../layout/WorkPageLayout";
import { CompleteWorkOrderModal } from "../snowflakes/work-orders/CompleteWorkOrderModal";
import { HistorySection } from "../snowflakes/work-orders/HistorySection";
import { ProcedureSection } from "../snowflakes/work-orders/ProcedureSection";
import {
  canCancelWorkOrder,
  canDeleteWorkOrder,
  displayWorkOrderNumber,
} from "../snowflakes/work-orders/presentation";
import {
  useWorkOrderAttachmentUpload,
  type WorkOrderAttachmentUpload,
} from "../snowflakes/work-orders/useWorkOrderAttachmentUpload";
import { WorkOrderAttachmentsSection } from "../snowflakes/work-orders/WorkOrderAttachmentsSection";
import {
  EditDetailsForm,
  type EditDraft,
  editDraftFieldsChanged,
  editDraftTitleError,
  isPlanningReadOnly,
  planningUpdateFields,
  rebaseEditDraft,
  toPlanningEditDraft,
  toScheduleEditDraft,
} from "../snowflakes/work-orders/WorkOrderEditForm";
import { WorkOrderOverview } from "../snowflakes/work-orders/WorkOrderOverview";
import { WorkOrderTimeAndCostsSection } from "../snowflakes/work-orders/WorkOrderTimeEntriesSection";
import { UnsavedChangesDialog } from "../UnsavedChangesDialog";
import { useUnsavedChangesGuard } from "../useUnsavedChangesGuard";
import { BulkActionsToolbar } from "./BulkActionsToolbar";
import { BulkAssignToMeModal } from "./BulkAssignToMeModal";
import { BulkDeleteWorkOrdersModal } from "./BulkDeleteWorkOrdersModal";
import { BulkMarkAsDoneModal } from "./BulkMarkAsDoneModal";
import { CancelWorkOrderModal } from "./CancelWorkOrderModal";
import { DeleteWorkOrderModal } from "./DeleteWorkOrderModal";
import type { PendingProcedureSave } from "./ProcedureExecutionLayout";
import { WorkOrderAssigneesField } from "./WorkOrderAssigneesField";
import {
  type RefreshedWorkOrderDraft,
  WorkOrderDraftRefreshDialog,
} from "./WorkOrderDraftRefreshDialog";
import { WorkOrderRow } from "./WorkOrderRow";
import { WorkOrderStaleSaveNotice } from "./WorkOrderStaleSaveNotice";
import { WorkOrderStatusMenu } from "./WorkOrderStatusMenu";

function toEditDraft(
  workOrder: WorkOrderDetail,
  assignees: string[],
  procedureId: string | null,
): EditDraft {
  return {
    version: workOrder.version,
    ...toPlanningEditDraft(workOrder),
    description: workOrder.description ?? "",
    instructions: workOrder.instructions ?? "",
    priorityCode: workOrder.priority?.code ?? null,
    statusCode: workOrder.status.code,
    workTypeCode: workOrder.workType.code,
    plannerGroupCode: workOrder.plannerGroup?.code ?? null,
    ...toScheduleEditDraft(workOrder.schedule),
    assignees,
    procedureId,
  };
}

function WorkOrderDetailPane({
  workOrderId,
  onOpenFullPage,
  onDeleted,
  facilityTimeZone,
  originSearch,
  leaveGuardRef,
}: {
  workOrderId: WorkOrderId;
  onOpenFullPage: (workOrderId: WorkOrderId) => void;
  onDeleted?: (workOrderId: WorkOrderId) => void;
  facilityTimeZone?: string;
  originSearch?: WorkOrdersLocationSearch;
  leaveGuardRef: RefObject<((onLeave: () => void) => void) | null>;
}) {
  const query = useWorkOrderDetailQuery(workOrderId);
  const lookupQuery = useWorkOrderLookupsQuery();
  const teamsUsers = useTeamsUsersQuery();
  const assignmentsQuery = useWorkOrderAssignmentsQuery(workOrderId);
  const assignmentMutations = useWorkOrderAssignmentMutations(workOrderId);
  const updateMutation = useWorkOrderUpdateMutation();
  const procedureAttachmentQuery = useProcedureAttachmentQuery(workOrderId);
  const procedureUpdateMutation =
    useUpdateProcedureAttachmentMutation(workOrderId);
  const procedureExecutionQuery = useProcedureExecutionQuery(
    workOrderId,
    Boolean(procedureAttachmentQuery.data),
  );
  const completeMutation = useCompleteWorkOrderMutation();
  const cancelMutation = useCancelWorkOrderMutation();
  const deleteMutation = useDeleteWorkOrderMutation();
  const { mutate: markWorkOrderViewed } = useMarkWorkOrderViewedMutation();
  // Opening a Work Order in the pane is what clears its unread item, rather
  // than the detail fetch, which the header also triggers.
  useEffect(() => {
    markWorkOrderViewed(workOrderId);
  }, [markWorkOrderViewed, workOrderId]);
  // Owned by the pane so a pending attachment survives tab switches and Edit.
  const attachmentUpload = useWorkOrderAttachmentUpload(workOrderId);
  const workOrder = query.data?.workOrder;
  const assignments = assignmentsQuery.data?.assignments ?? [];
  const [isEditing, setIsEditing] = useState(false);
  const [editDraft, setEditDraft] = useState<EditDraft | null>(null);
  const [editBaseline, setEditBaseline] = useState<EditDraft | null>(null);
  const editSession = useRef(0);
  const [pendingRefresh, setPendingRefresh] =
    useState<RefreshedWorkOrderDraft | null>(null);
  // biome-ignore lint/correctness/useExhaustiveDependencies: Changing records must invalidate pending refresh results as well as unmounting.
  useEffect(
    () => () => {
      editSession.current += 1;
    },
    [workOrderId],
  );
  const unsavedChangesDialog = useUnsavedChangesGuard({
    hasUnsavedChanges: Boolean(
      isEditing &&
        editDraft &&
        editBaseline &&
        JSON.stringify({ ...editDraft, version: editBaseline.version }) !==
          JSON.stringify(editBaseline),
    ),
  });
  const { requestLeave, allowNavigation } = unsavedChangesDialog;
  useEffect(() => {
    leaveGuardRef.current = (onLeave) =>
      requestLeave(() => {
        allowNavigation();
        onLeave();
      });
    return () => {
      leaveGuardRef.current = null;
    };
  }, [leaveGuardRef, requestLeave, allowNavigation]);
  const fieldUpdateSaved = useRef(false);
  const procedureDiscardConfirmed = useRef(false);
  const editStartAttachment = useRef<{
    attachmentId: number | null;
    procedureId: string | null;
  } | null>(null);
  const [attachmentConflict, setAttachmentConflict] = useState(false);
  const [confirmProcedureDiscard, setConfirmProcedureDiscard] = useState(false);
  const [isCompletionOpen, setIsCompletionOpen] = useState(false);
  const [hasPendingTabTimeChanges, setHasPendingTabTimeChanges] =
    useState(false);
  const [isCancellationOpen, setIsCancellationOpen] = useState(false);
  const [isDeletionOpen, setIsDeletionOpen] = useState(false);
  const pendingProcedureSave = useRef<PendingProcedureSave | null>(null);
  const prepareCompletion = usePrepareWorkOrderCompletionMutation(
    workOrderId,
    pendingProcedureSave,
  );
  // Server-computed, so a stale local Role never keeps an edit affordance.
  // Reopen alone opens the form with only the status editable.
  const canEditFields = workOrder?.actions.EDIT ?? false;
  const canEdit = canEditFields || (workOrder?.actions.REOPEN ?? false);

  function setCancellationOpen(open: boolean) {
    cancelMutation.reset();
    setIsCancellationOpen(open);
  }

  function setDeletionOpen(open: boolean) {
    deleteMutation.reset();
    setIsDeletionOpen(open);
  }

  // The list refetch after delete drops this row, and the screen then selects
  // the next Work Order, so the pane needs no navigation of its own.
  async function confirmDeletion() {
    await deleteMutation.mutateAsync({ workOrderId });
    setIsDeletionOpen(false);
    // The list drops this row, so the screen must move the selection off it.
    onDeleted?.(workOrderId);
  }

  function startEditing() {
    editSession.current += 1;
    setPendingRefresh(null);
    if (
      workOrder &&
      !assignmentsQuery.isPending &&
      procedureAttachmentQuery.isSuccess
    ) {
      fieldUpdateSaved.current = false;
      procedureUpdateMutation.reset();
      updateMutation.reset();
      const attachment = procedureAttachmentQuery.data;
      editStartAttachment.current = {
        attachmentId: attachment?.attachmentId ?? null,
        procedureId: attachment?.procedureId ?? null,
      };
      setAttachmentConflict(false);
      const draft = toEditDraft(
        workOrder,
        assignments.map(assigneeValue),
        procedureAttachmentQuery.data?.procedureId ?? null,
      );
      setEditDraft(draft);
      setEditBaseline(draft);
      setIsEditing(true);
    }
  }

  function cancelEditing() {
    fieldUpdateSaved.current = false;
    procedureDiscardConfirmed.current = false;
    editStartAttachment.current = null;
    setAttachmentConflict(false);
    setConfirmProcedureDiscard(false);
    editSession.current += 1;
    setPendingRefresh(null);
    setIsEditing(false);
    setEditDraft(null);
    setEditBaseline(null);
    updateMutation.reset();
    procedureUpdateMutation.reset();
  }

  async function saveEditing() {
    if (!editDraft || !workOrder) return;
    const planningReadOnly = isPlanningReadOnly(workOrder.status.code);
    // A blank Title is flagged inline; nothing is sent and every other value
    // stays in the form.
    if (canEditFields && !planningReadOnly && editDraftTitleError(editDraft))
      return;
    procedureUpdateMutation.reset();
    const originalAttachment = editStartAttachment.current;
    const currentAttachment = procedureAttachmentQuery.data;
    const originalProcedureId = originalAttachment?.procedureId ?? null;
    const procedureChanged = editDraft.procedureId !== originalProcedureId;
    if (
      procedureChanged &&
      originalAttachment &&
      (originalAttachment.attachmentId !==
        (currentAttachment?.attachmentId ?? null) ||
        originalAttachment.procedureId !==
          (currentAttachment?.procedureId ?? null))
    ) {
      setAttachmentConflict(true);
      return;
    }
    const hasProcedureResponses =
      (procedureExecutionQuery.data?.responses.length ?? 0) > 0;
    if (
      procedureChanged &&
      hasProcedureResponses &&
      !procedureDiscardConfirmed.current
    ) {
      setConfirmProcedureDiscard(true);
      return;
    }
    const savedDraft = toEditDraft(
      workOrder,
      editDraft.assignees,
      originalProcedureId,
    );
    const assigneesEdited =
      canEditFields &&
      workOrder.actions.ASSIGN &&
      assigneesChanged(assignments, editDraft.assignees);
    const assignmentOnlyEdit =
      assigneesEdited &&
      !editDraftFieldsChanged(editDraft, savedDraft, canEditFields, false);
    // The field update goes first because it is version-checked; assignment
    // changes follow it, and nothing is changed if the update is refused. An
    // assignment-only edit skips it so the version and history are untouched.
    try {
      if (!fieldUpdateSaved.current && !assignmentOnlyEdit) {
        await updateMutation.mutateAsync(
          canEditFields
            ? {
                workOrderId,
                workOrderNumber: workOrder.number,
                version: editDraft.version,
                ...(planningReadOnly ? {} : planningUpdateFields(editDraft)),
                description: editDraft.description,
                instructions: editDraft.instructions,
                priorityCode: editDraft.priorityCode,
                statusCode: editDraft.statusCode,
                workTypeCode: editDraft.workTypeCode,
                plannerGroupCode: editDraft.plannerGroupCode,
                basicStartDate: editDraft.startDate
                  ? new Date(editDraft.startDate).toISOString()
                  : null,
                dueDate: editDraft.dueDate
                  ? new Date(editDraft.dueDate).toISOString()
                  : null,
              }
            : {
                workOrderId,
                workOrderNumber: workOrder.number,
                version: editDraft.version,
                statusCode: editDraft.statusCode,
              },
        );
        fieldUpdateSaved.current = true;
      }
    } catch {
      return;
    }
    if (procedureChanged) {
      try {
        const updatedAttachment = await procedureUpdateMutation.mutateAsync({
          procedureId: editDraft.procedureId,
          discardResponses: procedureDiscardConfirmed.current,
          expectedAttachmentId: originalAttachment?.attachmentId ?? null,
        });
        editStartAttachment.current = {
          attachmentId: updatedAttachment?.attachmentId ?? null,
          procedureId: updatedAttachment?.procedureId ?? null,
        };
        procedureDiscardConfirmed.current = false;
        setAttachmentConflict(false);
      } catch (error) {
        if (
          error instanceof Error &&
          "code" in error &&
          error.code === "CONFLICT"
        ) {
          setAttachmentConflict(true);
        }
        if (
          error instanceof Error &&
          "code" in error &&
          error.code === "RESPONSES_EXIST" &&
          !procedureDiscardConfirmed.current
        ) {
          setConfirmProcedureDiscard(true);
        }
        await refreshDraftVersion();
        return;
      }
    }
    if (assigneesEdited) {
      try {
        await assignmentMutations.replace.mutateAsync({
          current: assignments,
          next: editDraft.assignees,
        });
      } catch {
        // The mutation has already shown its error toast. Reload so a retry
        // diffs against what was actually saved and uses the new version.
        const [detailResult] = await Promise.all([
          query.refetch(),
          assignmentsQuery.refetch(),
        ]);
        const freshWorkOrder = detailResult.data?.workOrder;
        if (freshWorkOrder) {
          setEditDraft((current) =>
            current ? { ...current, version: freshWorkOrder.version } : current,
          );
        }
        return;
      }
    }
    fieldUpdateSaved.current = false;
    procedureDiscardConfirmed.current = false;
    editStartAttachment.current = null;
    setConfirmProcedureDiscard(false);
    editSession.current += 1;
    setPendingRefresh(null);
    setIsEditing(false);
    setEditDraft(null);
    setEditBaseline(null);
  }
  async function refreshDraftVersion() {
    const result = await query.refetch();
    if (result.isSuccess && result.data?.workOrder) {
      setEditDraft((current) =>
        current
          ? { ...current, version: result.data.workOrder.version }
          : current,
      );
    }
  }
  function refreshDraft() {
    if (!editBaseline) return;
    const session = ++editSession.current;
    const baseline = editBaseline;
    setPendingRefresh(null);
    void Promise.all([
      query.refetch(),
      procedureAttachmentQuery.refetch(),
      assignmentsQuery.refetch(),
    ]).then(([result, attachmentResult, assignmentResult]) => {
      if (
        session !== editSession.current ||
        !result.isSuccess ||
        !result.data?.workOrder ||
        !attachmentResult.isSuccess ||
        !assignmentResult.isSuccess
      )
        return;
      const attachment = attachmentResult.data;
      setPendingRefresh({
        session,
        baseline,
        latest: toEditDraft(
          result.data.workOrder,
          (assignmentResult.data?.assignments ?? []).map(assigneeValue),
          attachment?.procedureId ?? null,
        ),
        attachment: {
          attachmentId: attachment?.attachmentId ?? null,
          procedureId: attachment?.procedureId ?? null,
        },
      });
    });
  }
  function resolveRefresh(resolution: "mine" | "latest") {
    if (!pendingRefresh || pendingRefresh.session !== editSession.current)
      return;
    fieldUpdateSaved.current = false;
    procedureDiscardConfirmed.current = false;
    editStartAttachment.current = pendingRefresh.attachment;
    setAttachmentConflict(false);
    updateMutation.reset();
    procedureUpdateMutation.reset();
    setEditBaseline(pendingRefresh.latest);
    setEditDraft((current) =>
      current
        ? rebaseEditDraft(
            current,
            pendingRefresh.baseline,
            pendingRefresh.latest,
            resolution,
          )
        : current,
    );
    setPendingRefresh(null);
  }

  if (query.isPending && !workOrder) {
    return <Text tone="subtle">Loading Work Order…</Text>;
  }

  if (!workOrder) {
    return (
      <Alert
        title="Unable to load this Work Order."
        tone="negative"
        action={
          <Button
            onClick={() => void query.refetch()}
            size="sm"
            variant="outline"
          >
            Retry
          </Button>
        }
      />
    );
  }

  return (
    <>
      <WorkOrderDetailContent
        canCancel={canCancelWorkOrder(workOrder)}
        onRequestCancel={() => setCancellationOpen(true)}
        canDelete={canDeleteWorkOrder(workOrder)}
        onRequestDelete={() => setDeletionOpen(true)}
        assignments={assignments}
        assignmentsLoading={
          assignmentsQuery.isPending || !procedureAttachmentQuery.isSuccess
        }
        attachmentUpload={attachmentUpload}
        canEdit={canEdit}
        canEditFields={canEditFields}
        currentUserId={teamsUsers.data?.currentUserId ?? null}
        editDraft={editDraft}
        facilityTimeZone={facilityTimeZone}
        isEditing={isEditing}
        lookupData={lookupQuery.data}
        onOpenFullPage={(id) =>
          requestLeave(() => {
            allowNavigation();
            onOpenFullPage(id);
          })
        }
        pendingProcedureSave={pendingProcedureSave}
        procedureAttachmentError={procedureAttachmentQuery.isError}
        attachmentConflict={attachmentConflict}
        onRetryProcedureAttachment={() =>
          void procedureAttachmentQuery.refetch()
        }
        onCancelEdit={cancelEditing}
        onChangeEdit={(update) => {
          if (
            Object.keys(update).some(
              (key) => key !== "assignees" && key !== "procedureId",
            )
          )
            fieldUpdateSaved.current = false;
          setEditDraft((current) =>
            current ? { ...current, ...update } : current,
          );
        }}
        onEdit={startEditing}
        onRefreshDraft={refreshDraft}
        completionPending={prepareCompletion.isPending}
        onRequestComplete={() => {
          if (prepareCompletion.isPending) return;
          prepareCompletion.mutate(undefined, {
            onSuccess: () => setIsCompletionOpen(true),
          });
        }}
        onTabTimePendingChange={setHasPendingTabTimeChanges}
        onSaveEdit={() => void saveEditing()}
        onStatusUpdateError={() => void query.refetch()}
        originSearch={originSearch}
        workOrder={workOrder}
        updateError={
          updateMutation.error instanceof WorkOrderUpdateError &&
          updateMutation.error.code === "WORK_ORDER_STALE"
            ? updateMutation.error
            : (procedureUpdateMutation.error ?? updateMutation.error)
        }
        updatePending={
          updateMutation.isPending ||
          procedureUpdateMutation.isPending ||
          assignmentMutations.replace.isPending
        }
      />
      <UnsavedChangesDialog {...unsavedChangesDialog} />
      {isEditing &&
      editDraft &&
      pendingRefresh &&
      pendingRefresh.session === editSession.current ? (
        <WorkOrderDraftRefreshDialog
          draft={editDraft}
          refresh={pendingRefresh}
          onResolve={resolveRefresh}
          onCancel={() => {
            editSession.current += 1;
            setPendingRefresh(null);
          }}
        />
      ) : null}
      <CompleteWorkOrderModal
        onComplete={async (input) => {
          // Completion validates saved Procedure answers, so save any
          // unsaved ones first.
          await pendingProcedureSave.current?.();
          await completeMutation.mutateAsync({
            ...input,
            workOrderId: workOrder.id,
          });
        }}
        onOpenChange={setIsCompletionOpen}
        open={isCompletionOpen}
        canEditWorkOrder={workOrder.actions.EDIT}
        canAddTimeForCompletionReview={workOrder.actions.COMPLETE}
        hasPendingTimeChanges={hasPendingTabTimeChanges}
        workOrder={{
          completionNote: workOrder.completionNote,
          id: workOrder.id,
          number: workOrder.number,
          title: workOrder.title,
          totalCost: workOrder.costs.total,
        }}
      />
      <CancelWorkOrderModal
        error={
          cancelMutation.error instanceof Error
            ? cancelMutation.error.message
            : null
        }
        isPending={cancelMutation.isPending}
        number={workOrder.number}
        onCancel={(reason) =>
          cancelMutation
            .mutateAsync({
              workOrderId: workOrder.id,
              cancellationReason: reason,
            })
            .then(() => undefined)
        }
        onOpenChange={setCancellationOpen}
        open={isCancellationOpen}
      />
      <DeleteWorkOrderModal
        error={
          deleteMutation.error instanceof Error
            ? deleteMutation.error.message
            : null
        }
        isPending={deleteMutation.isPending}
        number={workOrder.number}
        onDelete={confirmDeletion}
        onOpenChange={setDeletionOpen}
        open={isDeletionOpen}
      />
      <Modal
        open={confirmProcedureDiscard}
        onOpenChange={(open) => {
          if (!open) setConfirmProcedureDiscard(false);
        }}
        title="Discard saved Procedure responses?"
        description="Replacing or removing this Procedure will permanently remove its saved answers and photos."
        footer={
          <>
            <Button
              onClick={() => setConfirmProcedureDiscard(false)}
              size="sm"
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                procedureDiscardConfirmed.current = true;
                setConfirmProcedureDiscard(false);
                void saveEditing();
              }}
              size="sm"
              intent="destructive"
            >
              Discard responses and save
            </Button>
          </>
        }
      >
        <Text>
          This action cannot be undone. Confirm only if the existing Procedure
          responses are no longer needed.
        </Text>
      </Modal>
    </>
  );
}

function WorkOrderDetailContent({
  completionPending,
  workOrder,
  onOpenFullPage,
  canEdit,
  canEditFields,
  currentUserId,
  assignments,
  assignmentsLoading,
  attachmentUpload,
  attachmentConflict,
  editDraft,
  facilityTimeZone,
  isEditing,
  lookupData,
  onCancelEdit,
  onChangeEdit,
  onEdit,
  onRefreshDraft,
  onRequestComplete,
  onTabTimePendingChange,
  onSaveEdit,
  onStatusUpdateError,
  onRetryProcedureAttachment,
  procedureAttachmentError,
  originSearch,
  pendingProcedureSave,
  updateError,
  updatePending,
  canCancel,
  onRequestCancel,
  canDelete,
  onRequestDelete,
}: {
  canCancel: boolean;
  onRequestCancel: () => void;
  canDelete: boolean;
  onRequestDelete: () => void;
  originSearch?: WorkOrdersLocationSearch;
  workOrder: WorkOrderDetail;
  onOpenFullPage: (workOrderId: WorkOrderId) => void;
  pendingProcedureSave: RefObject<PendingProcedureSave | null>;
  canEdit: boolean;
  canEditFields: boolean;
  currentUserId: number | null;
  assignments: WorkOrderAssignment[];
  assignmentsLoading: boolean;
  attachmentUpload: WorkOrderAttachmentUpload;
  attachmentConflict: boolean;
  editDraft: EditDraft | null;
  facilityTimeZone?: string;
  isEditing: boolean;
  lookupData: ReturnType<typeof useWorkOrderLookupsQuery>["data"];
  onCancelEdit: () => void;
  onChangeEdit: (update: Partial<EditDraft>) => void;
  onEdit: () => void;
  onRefreshDraft: () => void;
  onRequestComplete: () => void;
  completionPending: boolean;
  onTabTimePendingChange: (pending: boolean) => void;
  onSaveEdit: () => void;
  onStatusUpdateError: () => void;
  onRetryProcedureAttachment: () => void;
  procedureAttachmentError: boolean;
  updateError: Error | null;
  updatePending: boolean;
}) {
  const [activeTab, setActiveTab] = useState("details");

  return (
    <div className="flex h-full min-h-0 flex-col gap-(--spacing-16)">
      <div className="shrink-0">
        <Group align="start" justify="between" wrap="wrap">
          <Stack gap={4}>
            <Title as="h2" size="lg">
              {workOrder.title}
            </Title>
            <Text tone="informative">
              {displayWorkOrderNumber(workOrder.number)}
            </Text>
          </Stack>
          <Group gap={8}>
            {!isEditing ? (
              <Button
                onClick={() => onOpenFullPage(workOrder.id)}
                size="sm"
                variant="outline"
              >
                Open full page
              </Button>
            ) : null}
            {canEdit &&
            (activeTab === "details" ||
              activeTab === "procedure" ||
              isEditing) ? (
              isEditing ? (
                <Group gap={8}>
                  <Button onClick={onCancelEdit} size="sm" variant="ghost">
                    Cancel
                  </Button>
                  <Button
                    disabled={
                      !editDraft ||
                      (canEditFields &&
                        !isPlanningReadOnly(workOrder.status.code) &&
                        Boolean(editDraftTitleError(editDraft)))
                    }
                    loading={updatePending}
                    loadingText="Saving"
                    onClick={onSaveEdit}
                    size="sm"
                  >
                    Save
                  </Button>
                </Group>
              ) : (
                <Button
                  disabled={assignmentsLoading}
                  icon="edit-pen"
                  onClick={onEdit}
                  size="sm"
                  variant="outline"
                >
                  Edit
                </Button>
              )
            ) : null}
            {!isEditing && (canCancel || canDelete) ? (
              <MenuButton
                aria-label="More Work Order actions"
                items={[
                  ...(canCancel
                    ? [
                        {
                          id: "cancel",
                          label: "Cancel Work Order",
                          leadingIcon: "x-close" as const,
                          onSelect: onRequestCancel,
                        },
                      ]
                    : []),
                  ...(canDelete
                    ? [
                        {
                          id: "delete",
                          label: "Delete Work Order",
                          leadingIcon: "trash-delete-bin" as const,
                          onSelect: onRequestDelete,
                          tone: "destructive" as const,
                        },
                      ]
                    : []),
                ]}
                size="sm"
                variant="overflow"
              />
            ) : null}
          </Group>
        </Group>

        {procedureAttachmentError ? (
          <Alert
            action={
              <Button
                onClick={onRetryProcedureAttachment}
                size="sm"
                variant="outline"
              >
                Retry
              </Button>
            }
            title="Unable to load the attached Procedure. Edit is unavailable until it loads."
            tone="negative"
          />
        ) : null}

        <Tabs
          onValueChange={(value) => value && setActiveTab(value)}
          value={activeTab}
        >
          <TabsList aria-label="Work Order detail sections">
            <TabsTrigger value="details">Details</TabsTrigger>
            <TabsTrigger value="procedure">Procedure</TabsTrigger>
            <TabsTrigger value="time-costs">Time &amp; Costs</TabsTrigger>
            <TabsTrigger
              chipLabel={workOrder.history?.length || undefined}
              value="history"
            >
              History
            </TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto pb-(--spacing-24)">
        <div className="flex flex-col gap-(--spacing-16)">
          {activeTab === "details" ? (
            <>
              {!isEditing &&
              workOrder.status.code === "CANCELED" &&
              workOrder.cancellationReason ? (
                <Alert
                  title="Work Order Cancelled"
                  subtext={workOrder.cancellationReason}
                  tone="negative"
                />
              ) : null}
              {!isEditing &&
              workOrder.status.code === "COMPLETED" &&
              workOrder.completionNote ? (
                <Alert
                  title="Completion notes"
                  subtext={workOrder.completionNote}
                />
              ) : null}
              {isEditing && editDraft ? (
                <EditDetailsForm
                  workOrder={workOrder}
                  attachments={
                    <WorkOrderAttachmentsSection
                      isCanceled={workOrder.status.code === "CANCELED"}
                      isCompleted={workOrder.status.code === "COMPLETED"}
                      upload={attachmentUpload}
                      workOrderId={workOrder.id}
                    />
                  }
                  allowedStatusCodes={
                    workOrder.actions.TRANSITION
                      ? undefined
                      : {
                          savedCode: workOrder.status.code,
                          targets: workOrder.statusTargets,
                        }
                  }
                  draft={editDraft}
                  lookupData={lookupData}
                  onChange={onChangeEdit}
                  planningReadOnly={isPlanningReadOnly(workOrder.status.code)}
                  reopenOnly={!canEditFields}
                  assigneesField={
                    canEditFields && workOrder.actions.ASSIGN ? (
                      <WorkOrderAssigneesField
                        assignments={assignments}
                        canAddSelf={workOrder.actions.CLAIM}
                        currentUserId={currentUserId}
                        mode="edit"
                        onChange={(value) => onChangeEdit({ assignees: value })}
                        selfOnly={!workOrder.actions.TRANSITION}
                        value={editDraft.assignees}
                      />
                    ) : (
                      <WorkOrderAssigneesField
                        assignments={assignments}
                        mode="view"
                      />
                    )
                  }
                />
              ) : (
                <WorkOrderOverview
                  workOrder={workOrder}
                  facilityTimeZone={facilityTimeZone}
                  originSearch={originSearch}
                  statusControl={
                    <WorkOrderStatusMenu
                      onRequestComplete={onRequestComplete}
                      completionPending={completionPending}
                      onUpdateError={onStatusUpdateError}
                      workOrder={workOrder}
                    />
                  }
                  assigneesField={
                    <WorkOrderAssigneesField
                      assignments={assignments}
                      mode="view"
                    />
                  }
                  attachments={
                    <WorkOrderAttachmentsSection
                      isCanceled={workOrder.status.code === "CANCELED"}
                      isCompleted={workOrder.status.code === "COMPLETED"}
                      upload={attachmentUpload}
                      workOrderId={workOrder.id}
                    />
                  }
                />
              )}
            </>
          ) : null}
          {/* Stays mounted while other tabs are shown so unsaved Procedure
              answers survive a tab change and are saved before completion. */}
          <div hidden={activeTab !== "procedure"}>
            <ProcedureSection
              canAttach={workOrder.actions.ATTACH_PROCEDURE}
              isEditing={isEditing && workOrder.actions.ATTACH_PROCEDURE}
              instructions={workOrder.instructions}
              onProcedureChange={(procedureId) => onChangeEdit({ procedureId })}
              pendingSaveRef={pendingProcedureSave}
              selectedProcedureId={editDraft?.procedureId}
              workOrderId={workOrder.id}
              workOrderNumber={workOrder.number}
              workOrderStatusCode={workOrder.status.code}
              workOrderTitle={workOrder.title}
            />
          </div>
          <div hidden={activeTab !== "time-costs"}>
            <WorkOrderTimeAndCostsSection
              canAddTimeToCompleted={
                workOrder.status.code === "COMPLETED" &&
                workOrder.actions.REOPEN
              }
              canEditWorkOrder={workOrder.actions.EDIT}
              onPendingChange={onTabTimePendingChange}
              workOrderId={workOrder.id}
            />
          </div>
          {activeTab === "history" ? (
            <div className="mt-(--spacing-16)">
              <HistorySection history={workOrder.history ?? []} />
            </div>
          ) : null}
          {attachmentConflict ? (
            <Alert
              action={
                <Button onClick={onCancelEdit} size="sm" variant="outline">
                  Cancel edit
                </Button>
              }
              title="The attached Procedure changed while you were editing. Cancel and reopen Edit to load the latest Procedure before replacing it."
              tone="negative"
            />
          ) : null}
          {updateError ? (
            <div className="border-t border-outline-subtle px-(--spacing-20) py-(--spacing-12)">
              {updateError instanceof WorkOrderUpdateError &&
              updateError.code === "WORK_ORDER_STALE" ? (
                <WorkOrderStaleSaveNotice onRefresh={onRefreshDraft} />
              ) : (
                <Text size="sm" tone="negative">
                  {updateError.message}
                </Text>
              )}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export function WorkOrderSplitView({
  orders,
  emptyState,
  selectedWorkOrderId,
  onOpenFullPage,
  onDeleted,
  onSelectWorkOrder,
  canMarkDone,
  canAssignToMe,
  canDelete,
  facilityTimeZone,
  hasMore,
  isLoadingMore,
  isLoadMoreError,
  originSearch,
  prioritySortDirection,
  onShowMore,
  onTogglePrioritySort,
}: {
  orders: WorkOrder[];
  emptyState?: ReactNode;
  selectedWorkOrderId: WorkOrderId | null;
  onOpenFullPage: (workOrderId: WorkOrderId) => void;
  onDeleted?: (workOrderId: WorkOrderId) => void;
  onSelectWorkOrder: (workOrderId: WorkOrderId) => void;
  canMarkDone: boolean;
  canAssignToMe: boolean;
  canDelete: boolean;
  facilityTimeZone?: string;
  hasMore?: boolean;
  isLoadingMore?: boolean;
  isLoadMoreError?: boolean;
  onShowMore?: () => void;
  prioritySortDirection: "asc" | "desc";
  onTogglePrioritySort: () => void;
  /** The Work Orders page's search, kept as list state on the Asset back link. */
  originSearch?: WorkOrdersLocationSearch;
}) {
  const leaveGuardRef = useRef<((onLeave: () => void) => void) | null>(null);
  const workOrderListRef = useRef<HTMLDivElement>(null);
  function selectWorkOrder(workOrderId: WorkOrderId) {
    if (workOrderId === selectedWorkOrderId) return;
    const select = () => onSelectWorkOrder(workOrderId);
    if (leaveGuardRef.current) leaveGuardRef.current(select);
    else select();
  }
  const prioritySortLabel =
    prioritySortDirection === "asc" ? "Highest first" : "Lowest first";
  const nextPrioritySortLabel =
    prioritySortDirection === "asc" ? "Lowest first" : "Highest first";
  const teamsUsers = useTeamsUsersQuery();
  const currentUserId = teamsUsers.data?.currentUserId ?? null;
  const [selectedIds, setSelectedIds] = useState<Set<WorkOrderId>>(
    () => new Set(),
  );
  const [activeBulkAction, setActiveBulkAction] = useState<
    "complete" | "assign" | "delete" | null
  >(null);

  useEffect(() => {
    setSelectedIds((current) => {
      if (current.size === 0) return current;
      const validIds = new Set(orders.map((order) => order.id));
      const next = new Set([...current].filter((id) => validIds.has(id)));
      return next.size === current.size ? current : next;
    });
  }, [orders]);

  const selectedWorkOrderIsInList =
    selectedWorkOrderId !== null &&
    orders.some((order) => order.id === selectedWorkOrderId);

  useEffect(() => {
    if (!selectedWorkOrderId || !selectedWorkOrderIsInList) return;
    const selectedRow =
      workOrderListRef.current?.querySelector<HTMLButtonElement>(
        'button[aria-pressed="true"]',
      );
    selectedRow?.scrollIntoView?.({ block: "nearest" });
  }, [selectedWorkOrderId, selectedWorkOrderIsInList]);

  function toggleChecked(workOrderId: WorkOrderId, checked: boolean) {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (checked) {
        next.add(workOrderId);
      } else {
        next.delete(workOrderId);
      }
      return next;
    });
  }

  function clearSelection() {
    setSelectedIds(new Set());
  }

  function handleOutcome(outcome: BulkActionOutcome) {
    setSelectedIds((current) => {
      const next = new Set(current);
      for (const item of outcome.succeeded) {
        next.delete(item.id);
      }
      return next;
    });
  }

  const selectedOrders = orders.filter((order) => selectedIds.has(order.id));
  const bulkActionItems: MenuButtonItem[] = [];
  if (canMarkDone) {
    bulkActionItems.push({
      id: "mark-done",
      label: "Mark as Done",
      leadingIcon: "circle-check",
      onSelect: () => setActiveBulkAction("complete"),
    });
  }
  if (canAssignToMe) {
    bulkActionItems.push({
      id: "assign-to-me",
      label: "Assign to me",
      leadingIcon: "user",
      onSelect: () => setActiveBulkAction("assign"),
    });
  }
  if (canDelete) {
    bulkActionItems.push({
      id: "delete",
      label: "Delete",
      leadingIcon: "trash-delete-bin",
      tone: "destructive",
      onSelect: () => setActiveBulkAction("delete"),
    });
  }

  return (
    <>
      <WorkSplitView
        detail={
          <Tile hasBorder padding="compact" radius="md" width="full">
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
              {selectedWorkOrderId ? (
                <WorkOrderDetailPane
                  key={selectedWorkOrderId}
                  facilityTimeZone={facilityTimeZone}
                  leaveGuardRef={leaveGuardRef}
                  onDeleted={onDeleted}
                  onOpenFullPage={onOpenFullPage}
                  originSearch={originSearch}
                  workOrderId={selectedWorkOrderId}
                />
              ) : (
                <Text align="center" tone="subtle">
                  Select a Work Order to view its details.
                </Text>
              )}
            </div>
          </Tile>
        }
        layout="workOrders"
        list={
          <Tile hasBorder padding="none" radius="md" width="full">
            {selectedIds.size > 0 ? (
              <BulkActionsToolbar
                actions={bulkActionItems}
                onClear={clearSelection}
                selectedCount={selectedIds.size}
              />
            ) : null}
            {orders.length > 0 ? (
              <div className="flex shrink-0 items-center justify-between border-b border-outline-subtle px-(--spacing-16) py-(--spacing-12)">
                <Text size="sm" weight="medium" tone="subtle">
                  Sort by
                </Text>
                <Button
                  aria-label={`Priority, ${prioritySortLabel}. Click to sort ${nextPrioritySortLabel}.`}
                  onClick={onTogglePrioritySort}
                  size="sm"
                  variant="ghost"
                >
                  Priority · {prioritySortLabel}
                </Button>
              </div>
            ) : null}
            <div
              className="min-h-0 flex-1 overflow-y-auto"
              ref={workOrderListRef}
            >
              {orders.length === 0
                ? emptyState
                : orders.map((workOrder) => (
                    <WorkOrderRow
                      isChecked={selectedIds.has(workOrder.id)}
                      facilityTimeZone={facilityTimeZone}
                      key={workOrder.id}
                      onSelect={selectWorkOrder}
                      onToggleChecked={toggleChecked}
                      selected={selectedWorkOrderId === workOrder.id}
                      workOrder={workOrder}
                    />
                  ))}
              {orders.length > 0 && hasMore && onShowMore ? (
                <div className="flex flex-col items-center gap-(--spacing-8) px-(--spacing-16) py-(--spacing-16)">
                  {isLoadMoreError ? (
                    <Text size="sm" tone="negative">
                      Unable to load more work orders.
                    </Text>
                  ) : null}
                  <Button
                    loading={isLoadingMore ?? false}
                    loadingText="Loading"
                    onClick={onShowMore}
                    variant="outline"
                  >
                    Show more
                  </Button>
                </div>
              ) : null}
            </div>
          </Tile>
        }
      />
      <BulkMarkAsDoneModal
        onOpenChange={(open) => setActiveBulkAction(open ? "complete" : null)}
        onOutcome={handleOutcome}
        open={activeBulkAction === "complete"}
        workOrders={selectedOrders}
      />
      <BulkAssignToMeModal
        currentUserId={currentUserId}
        onOpenChange={(open) => setActiveBulkAction(open ? "assign" : null)}
        onOutcome={handleOutcome}
        open={activeBulkAction === "assign"}
        workOrders={selectedOrders}
      />
      <BulkDeleteWorkOrdersModal
        onOpenChange={(open) => setActiveBulkAction(open ? "delete" : null)}
        onOutcome={handleOutcome}
        open={activeBulkAction === "delete"}
        workOrders={selectedOrders}
      />
    </>
  );
}
